// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { BadgeCheck, BadgeMinus, Loader2, Power, PowerOff, TriangleAlert } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useToggleFirmwareActiveMutation, useToggleFirmwareProductionMutation } from "@/api/hooks/use-firmware";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface FirmwareToggleTarget {
  firmwareId: number;
  versionStr: string;
  isActive: boolean;
  isProduction: boolean;
}

interface FirmwareToggleDialogProps {
  target: FirmwareToggleTarget | null;
  field: "active" | "production";
  onClose: () => void;
}

export function FirmwareToggleDialog({ target, field, onClose }: FirmwareToggleDialogProps) {
  const { t } = useTranslation("back-office");
  const last = useRef(target);
  if (target) last.current = target;
  const display = target ?? last.current;
  const toggleActive = useToggleFirmwareActiveMutation();
  const toggleProduction = useToggleFirmwareProductionMutation();

  const isActiveField = field === "active";
  // The current "on" state for the field being toggled (deactivate / unmark are the destructive turn-offs).
  const turningOff = isActiveField ? !!display?.isActive : !!display?.isProduction;
  const pending = isActiveField ? toggleActive.isPending : toggleProduction.isPending;

  const titleLabel = isActiveField
    ? turningOff
      ? t("firmware.dialog.deactivateTitle")
      : t("firmware.dialog.activateTitle")
    : turningOff
      ? t("firmware.dialog.unmarkProductionTitle")
      : t("firmware.dialog.markProductionTitle");
  const promptLabel = isActiveField
    ? turningOff
      ? t("firmware.dialog.deactivatePrompt")
      : t("firmware.dialog.activatePrompt")
    : turningOff
      ? t("firmware.dialog.unmarkProductionPrompt")
      : t("firmware.dialog.markProductionPrompt");
  const confirmLabel = isActiveField
    ? turningOff
      ? t("firmware.dialog.deactivateConfirm")
      : t("firmware.dialog.activateConfirm")
    : turningOff
      ? t("firmware.dialog.unmarkProductionConfirm")
      : t("firmware.dialog.markProductionConfirm");

  const Icon = isActiveField ? (turningOff ? PowerOff : Power) : turningOff ? BadgeMinus : BadgeCheck;
  // Turning either flag off (deactivate / unmark production) is a destructive action.
  const destructive = turningOff;

  async function handleConfirm() {
    if (!display) return;
    try {
      if (isActiveField) {
        await toggleActive.mutateAsync({ firmwareId: display.firmwareId, isActive: !display.isActive });
      } else {
        await toggleProduction.mutateAsync({
          firmwareId: display.firmwareId,
          isProduction: !display.isProduction,
        });
      }
      toast.success(`${confirmLabel}: ${display.versionStr}`);
      onClose();
    } catch {
      // Error toast handled by API middleware
    }
  }

  return (
    <Dialog open={!!target} onOpenChange={() => onClose()}>
      <DialogContent id="dialog-toggle-ota" className="sm:max-w-[33rem] p-0 gap-0 overflow-hidden flex flex-col">
        <div className="flex flex-row">
          <WizardPane variant={destructive ? "destructive" : "default"} icon={Icon} />
          <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
            <DialogHeader>
              <DialogTitle>{titleLabel}</DialogTitle>
              <DialogDescription asChild>
                <div>
                  <p>
                    {promptLabel}
                    <br />
                    <span className="font-semibold text-foreground">{display?.versionStr}</span>?
                  </p>
                  {isActiveField && turningOff && (
                    <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                      <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
                      <span className="text-sm">{t("firmware.dialog.deactivateWarning")}</span>
                    </div>
                  )}
                </div>
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>
        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button variant="outline" onClick={onClose}>
            {t("buttons.cancel")}
          </Button>
          <Button
            variant={destructive ? "destructive" : "default"}
            className={
              destructive ? "" : "bg-green-700 text-white hover:bg-green-800 dark:bg-green-700 dark:hover:bg-green-600"
            }
            onClick={handleConfirm}
            disabled={pending}
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
